import React, { useEffect, useState, useContext } from "react";
import {
  Link,
  useLocation,
  useSearchParams,
  useNavigate,
} from "react-router-dom";
import { createPortal } from "react-dom";
import "./../../../assets/css/admin/gestionArticles/AdminArticlesPage.css";
import Breadcrumb from "../../../components/breadcrumb/Breadcrumb";
import useArticles from "../../../hooks/useArticle";
import useCategorie from "../../../hooks/useCategorie";
import useUtilisateur from "../../../hooks/useUtilisateur";
import defaultArticleImage from "../../../assets/images/utilisateur/default.jpeg";
import { truncateText } from "../../../utils/formatTexte";
import { AuthContext } from "../../../contexts/AuthContext.js";

// Un seul statut "dominant" par article, calculé par ordre de priorité.
// Test > Vendu > Plus intéressé > Expiré > Approuvé / Non approuvé / En attente
const ARTICLE_STATES = {
  test: {
    key: "test",
    label: "Test",
    icon: "bi-cloud-fog",
    cls: "badgeTest",
  },
  sold: {
    key: "sold",
    label: "Vendu",
    icon: "bi-bag-check-fill",
    cls: "badgeSold",
  },
  notInterested: {
    key: "notInterested",
    label: "Plus intéressé",
    icon: "bi-x-circle",
    cls: "badgeNotInterested",
  },
  expired: {
    key: "expired",
    label: "Expiré",
    icon: "bi-clock-history",
    cls: "badgeExpired",
  },
  approved: {
    key: "approved",
    label: "Approuvé",
    icon: "bi-check2-circle",
    cls: "badgeApproved",
  },
  notApproved: {
    key: "notApproved",
    label: "Non approuvé",
    icon: "bi-x-circle",
    cls: "badgeNotApproved",
  },
  pending: {
    key: "pending",
    label: "En attente",
    icon: "bi-hourglass-split",
    cls: "badgePending",
  },
};

const computeArticleState = (a) => {
  const isTest = Number(a?.isTest) === 1;
  const isSold = Number(a?.adminApprouved) === 3;
  const isNotInterested = Number(a?.status) === 4;
  const isExpired =
    a?.dateExpiration && new Date(a.dateExpiration) < new Date();
  const ap = Number(a?.adminApprouved);

  if (isTest) return ARTICLE_STATES.test;
  if (isSold) return ARTICLE_STATES.sold;
  if (ap === 2) return ARTICLE_STATES.pending;
  if (ap === 0) return ARTICLE_STATES.notApproved;
  if (isNotInterested) return ARTICLE_STATES.notInterested;

  // Ici, l'article est forcément approuvé (ap === 1)
  if (isExpired) return ARTICLE_STATES.expired;
  return ARTICLE_STATES.approved;
};

export default function AdminArticlesPage({ setAlert }) {
  const { user } = useContext(AuthContext);
  const { findAllArticlesForCategory, updateArticleStatus } = useArticles();
  const { getAllCategories } = useCategorie();
  const { getAllUser } = useUtilisateur();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [articles, setArticles] = useState([]);
  const id = searchParams.get("id") || "";
  const [approvalMenuId, setApprovalMenuId] = useState(null);
  const [approvalMenuPos, setApprovalMenuPos] = useState(null);
  // Pagination serveur
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const [total, setTotal] = useState(0);
  const [expiration, setExpiration] = useState("all");

  // Filtres UI
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("1");
  const [archive, setArchive] = useState("2");
  const [etat, setEtat] = useState("all");
  const [typeOffre, setTypeOffre] = useState("all");
  const [typeAbonnement, setTypeAbonnement] = useState("all");
  const [adminApprouved, setAdminApprouved] = useState("all");
  const [userSelected, setUserSelected] = useState("all");
  const [sort, setSort] = useState("-createdAt");
  const [categories, setCategories] = useState("all");
  const [categoriesList, setCategoriesList] = useState([]);
  const [loadginApproval, setLoadingApproval] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    type: null, // status | archive
    article: null,
  });
  const [userSearch, setUserSearch] = useState("");
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [usersList, setUsersList] = useState([]);

  const [periode, setPeriode] = useState("all");
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  const [reactionTypes, setReactionTypes] = useState([]);
  const [reactionLevel, setReactionLevel] = useState("all");
  const [typeCompteFilter, setTypeCompteFilter] = useState("all");
  const REACTION_LEVEL_THRESHOLDS = {
    low: 5,
    medium: 20,
    high: 50,
  };

  const [statusFilters, setStatusFilters] = useState([]);

  const imageBaseUrl = process.env.REACT_APP_ARTICLE_IMAGE;
  const breadcrumbItems = id
    ? [
      { label: "Mon Compte", link: "/utilisateur/compte" },
      { label: "Signalements", link: "/admin/signalements" },
      { label: "Articles" },
    ]
    : [
      { label: "Mon Compte", link: "/utilisateur/compte" },
      { label: "Articles" },
    ];

  const [paiementModal, setPaiementModal] = useState({
    open: false,
    article: null,
  });

  const openPaiementModal = (article) => {
    setPaiementModal({ open: true, article });
  };

  const closePaiementModal = () => {
    setPaiementModal({ open: false, article: null });
  };

  const statusLabel = (n) => (Number(n) === 1 ? "actif" : "inactif");

  const archiveLabel = (n) => (Number(n) === 1 ? "archivé" : "non archivé");
  const archiveBadgeClass = (n) =>
    Number(n) === 1 ? "badgeArchived" : "badgeNotArchived";

  const aboLabel = (n) => (Number(n) === 1 ? "premium" : "standard");
  const aboBadgeClass = (n) =>
    Number(n) === 1 ? "badgePremium" : "badgeStandard";

  const approbationIcon = (v) => {
    const n = Number(v);
    if (n === 1) return "bi-check2-circle"; // Approuvé
    if (n === 0) return "bi-x-circle"; // Non approuvé
    if (n === 3) return "bi-bag-check-fill"; // Vendu
    return "bi-hourglass-split"; // En attente (2)
  };

  const paiementStatusLabel = (s) => {
    switch (Number(s)) {
      case 1:
        return "Payé";
      case 2:
        return "En attente";
      case 3:
        return "Échoué";
      case 4:
        return "Gratuit";
      default:
        return "—";
    }
  };

  const paiementStatusBadgeClass = (s) => {
    switch (Number(s)) {
      case 1:
        return "badgePaiementPaye";
      case 2:
        return "badgePaiementAttente";
      case 3:
        return "badgePaiementEchoue";
      case 4:
        return "badgePaiementGratuit";
      default:
        return "badgePaiementAttente";
    }
  };

  const paiementTypeLabel = (t) => {
    switch (Number(t)) {
      case 1:
        return "Abonnement";
      case 2:
        return "Réabonnement";
      default:
        return "—";
    }
  };

  const paiementMethodLabel = (m) => {
    switch (Number(m)) {
      case 1:
        return "MVola";
      case 2:
        return "Orange Money";
      case 3:
        return "Airtel Money";
      case 4:
        return "Carte";
      case 5:
        return "Gratuit";
      default:
        return "—";
    }
  };

  const toNumber = (v) => {
    const n = Number(String(v ?? "0").replace(/[^\d.-]/g, ""));
    return Number.isFinite(n) ? n : 0;
  };

  const hasDiscountInfo = (p) =>
    toNumber(p?.amountRemise) > 0 || !!String(p?.coupon || "").trim();

  const stateIconBtnClass = (stateKey) => {
    switch (stateKey) {
      case "test":
        return "test";
      case "sold":
        return "sold";
      case "notInterested":
        return "notInterested";
      case "expired":
        return "expired";
      case "approved":
        return "success";
      case "notApproved":
        return "danger";
      default: // pending
        return "warning";
    }
  };

  const resolveArticlePhoto = (photos) => {
    const first = Array.isArray(photos) ? photos[0] : null;
    if (!first) return defaultArticleImage;

    const p = String(first).trim();
    if (!p) return defaultArticleImage;
    if (/^https?:\/\//i.test(p)) return p;

    return imageBaseUrl ? `${imageBaseUrl}/${p}` : `/${p}`;
  };

  const formatDate = (d) => {
    if (!d) return "—";
    try {
      return new Date(d).toLocaleDateString("fr-FR");
    } catch {
      return "—";
    }
  };

  const buildParams = () => {
    const params = {
      page,
      limit: PAGE_SIZE,
      sort,
      isAdminGet: 1,
    };

    // Filtres serveur
    if (status !== "all") params.status = Number(status);
    if (archive !== "all") params.archive = Number(archive);
    if (etat !== "all") params.etat = Number(etat);
    if (typeOffre !== "all") params.typeOffre = Number(typeOffre);
    if (adminApprouved !== "all")
      params.adminApprouved = Number(adminApprouved);
    if (typeAbonnement !== "all")
      params.typeAbonnement = Number(typeAbonnement);
    if (categories !== "all") params.idCategorie = categories;
    if (userSelected !== "all") {
      params.userId = userSelected;
    }
    if (typeCompteFilter === "particulier") {
      params.userTypeCompte = 2; // à ajuster selon votre mapping réel typeCompte
    } else if (typeCompteFilter === "professionnel") {
      params.userTypeCompte = 1;
    } else if (typeCompteFilter === "test") {
      params.isTest = 1;
    }
    if (id) {
      params._id = id;
    }
    if (reactionTypes.length > 0) {
      params.reactionTypes = reactionTypes.join(",");
    }
    if (reactionTypes.length > 0 && reactionLevel !== "all") {
      params.reactionThreshold = REACTION_LEVEL_THRESHOLDS[reactionLevel];
    }

    // Recherche serveur
    if (q.trim()) {
      params.query = q.trim();
    }

    if (expiration === "expired") {
      params.dateExpirationBefore = new Date().toISOString();
    } else if (expiration === "active") {
      params.dateExpirationAfter = new Date().toISOString();
    }

    // 🔥 Filtre par période de création
    const now2 = new Date();
    if (periode === "today") {
      const start = new Date(
        now2.getFullYear(),
        now2.getMonth(),
        now2.getDate(),
      );
      params.dateCreatedAfter = start.toISOString();
    } else if (periode === "7j") {
      const start = new Date(now2);
      start.setDate(start.getDate() - 7);
      params.dateCreatedAfter = start.toISOString();
    } else if (periode === "30j") {
      const start = new Date(now2);
      start.setDate(start.getDate() - 30);
      params.dateCreatedAfter = start.toISOString();
    } else if (periode === "month") {
      const start = new Date(now2.getFullYear(), now2.getMonth(), 1);
      params.dateCreatedAfter = start.toISOString();
    } else if (periode === "custom") {
      if (dateDebut)
        params.dateCreatedAfter = new Date(dateDebut).toISOString();
      if (dateFin) {
        const end = new Date(dateFin);
        end.setHours(23, 59, 59, 999);
        params.dateCreatedBefore = end.toISOString();
      }
    }
    if (statusFilters.length > 0) {
      params.articleStates = statusFilters.join(",");
    }
    return params;
  };
  const fetchArticles = async (pageNumber = page) => {
    setLoading(true);
    try {
      const params = buildParams();
      params.page = pageNumber;
      if (id) {
        params._id = id;
      }

      const res = await findAllArticlesForCategory(pageNumber, params);
      // Si il y a categorie selectionner, il faut

      // Attendu: { data: [], total: number }
      const rows = Array.isArray(res?.data) ? res.data : [];
      const t = Number(res?.total ?? 0);
      setArticles(rows);
      setTotal(Number.isFinite(t) ? t : 0);
      setPage(pageNumber);
    } catch (e) {
      console.error(e);
      setArticles([]);
      setTotal(0);
      setAlert?.({
        type: "danger",
        message: "Impossible de récupérer la liste des articles.",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await getAllCategories();
      const data = res.data.data || [];
      // Mettre la liste en ordre alphabetique
      data.sort((a, b) => a.nom.localeCompare(b.nom));
      setCategoriesList(data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchUsers = async () => {
    try {
      let res = [];
      if (user?.role == 1) {
        res = await getAllUser({ limit: 1000 });
      }
      const data = res?.data?.data || [];
      // Mettre la liste en ordre alphabetique
      data.sort((a, b) => {
        const nameA = `${a.nom} ${a.prenom}`.trim().toLowerCase();
        const nameB = `${b.nom} ${b.prenom}`.trim().toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setUsersList(data);
    } catch (e) {
      console.error(e);
      setAlert?.({
        type: "danger",
        message: "Impossible de récupérer la liste des utilisateurs.",
      });
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchUsers();
  }, []);

  useEffect(() => {
    setPage(1);
    fetchArticles(1);
  }, [
    q,
    status,
    archive,
    etat,
    typeOffre,
    typeAbonnement,
    sort,
    adminApprouved,
    categories,
    userSelected,
    expiration,
    periode,
    dateDebut,
    dateFin,
    reactionTypes,
    reactionLevel,
    typeCompteFilter,
    statusFilters,
  ]);

  useEffect(() => {
    if (!approvalMenuId) return;
    const close = () => {
      setApprovalMenuId(null);
      setApprovalMenuPos(null);
    };
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    document.addEventListener("click", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      document.removeEventListener("click", close);
    };
  }, [approvalMenuId]);

  const approvalMenuArticle = articles.find(
    (x) => String(x._id) === String(approvalMenuId),
  );

  // Pagination calculée sur total serveur
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);

  const handleToggleStatus = async (a) => {
    try {
      const id = a?._id;
      if (!id) return;

      const next = Number(a?.status) === 1 ? 2 : 1;

      await updateArticleStatus(id, "status", next)
        .then((res) => {
          setArticles((prev) =>
            (prev || []).map((x) =>
              String(x._id) === String(id) ? { ...x, status: next } : x,
            ),
          );

          setAlert?.({
            type: "success",
            message: next === 1 ? "Article activé." : "Article désactivé.",
          });
        })
        .catch((err) => {
          const messageErreur = err?.response?.data?.error
            ? err?.response?.data?.error
            : "Action impossible pour le moment.";
          setAlert?.({
            type: "danger",
            message: messageErreur,
          });
        });
    } catch (e) {
      console.error(e);
      setAlert?.({
        type: "danger",
        message: "Action impossible pour le moment.",
      });
    }
  };

  const handleToggleArchive = async (a) => {
    try {
      const id = a?._id;
      if (!id) return;

      const next = Number(a?.archive) === 2 ? 1 : 2;

      await updateArticleStatus(id, "archive", next)
        .then((res) => {
          setArticles((prev) =>
            (prev || []).map((x) =>
              String(x._id) === String(id) ? { ...x, archive: next } : x,
            ),
          );

          setAlert?.({
            type: "success",
            message: next === 1 ? "Article archivé." : "Article désarchivé.",
          });
        })
        .catch((err) => {
          const messageErreur = err?.response?.data?.error
            ? err?.response?.data?.error
            : "Action impossible pour le moment.";
          setAlert?.({
            type: "danger",
            message: messageErreur,
          });
        });
    } catch (e) {
      console.error(e);
      setAlert?.({
        type: "danger",
        message: "Action impossible pour le moment.",
      });
    }
  };

  const handleSetApproval = async (a, value) => {
    try {
      setLoadingApproval(true);
      const id = a?._id;
      if (!id) return;

      await updateArticleStatus(id, "adminApprouved", value);

      setArticles((prev) =>
        prev.map((x) =>
          String(x._id) === String(id) ? { ...x, adminApprouved: value } : x,
        ),
      );

      setApprovalMenuId(null);
      setLoadingApproval(false);
      setAlert?.({
        type: "success",
        message:
          value === 1
            ? "Article approuvé."
            : value === 0
              ? "Article non approuvé."
              : value === 3
                ? "Article marqué comme vendu."
                : "Article mis en attente.",
      });
    } catch (e) {
      console.error(e);
      setLoadingApproval(false);
      setAlert?.({
        type: "danger",
        message: "Action impossible.",
      });
    }
  };

  const handleNotInterested = async (a, value) => {
    try {
      setLoadingApproval(true);
      const id = a?._id;
      if (!id) return;

      await updateArticleStatus(id, "status", value);

      setArticles((prev) =>
        prev.map((x) =>
          String(x._id) === String(id) ? { ...x, status: value } : x,
        ),
      );

      setApprovalMenuId(null);
      setLoadingApproval(false);
      setAlert?.({
        type: "success",
        message:
          value === 4
            ? "Article marqué comme 'N'est plus intéressé'."
            : "Article mis en attente.",
      });
    } catch (e) {
      console.error(e);
      setLoadingApproval(false);
      setAlert?.({
        type: "danger",
        message: "Action impossible.",
      });
    }
  };

  const openConfirmModal = (type, article) => {
    setConfirmModal({
      open: true,
      type,
      article,
    });
  };

  const closeConfirmModal = () => {
    setConfirmModal({
      open: false,
      type: null,
      article: null,
    });
  };

  const confirmAction = async () => {
    if (!confirmModal.article || !confirmModal.type) return;

    if (confirmModal.type === "status") {
      await handleToggleStatus(confirmModal.article);
    }

    if (confirmModal.type === "archive") {
      await handleToggleArchive(confirmModal.article);
    }

    closeConfirmModal();
  };

  const getVendeurName = (a) => {
    return a?.idUtilisateur?.nom || a?.utilisateur?.nom
      ? `${a?.idUtilisateur?.nom || a?.utilisateur?.nom || ""} ${a?.idUtilisateur?.prenom || a?.utilisateur?.prenom || ""
        }`.trim()
      : a?.pseudo || "—";
  };

  const escapeCsvValue = (value) => {
    const str = String(value ?? "—");
    if (/[",\n;]/.test(str)) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const handleExportCsv = async () => {
    try {
      setLoading(true);

      const params = buildParams(); // garde les filtres actuels tels quels
      params.page = 1;
      params.limit = 100000;
      params.isAdminGet = 1;

      if (id) {
        params._id = id;
      }

      const res = await findAllArticlesForCategory(1, params);
      const allArticles = Array.isArray(res?.data) ? res.data : [];

      const data = allArticles.map((a) => {
        const categorieName = a?.idCategorie?.nom || a?.categorie?.nom || "—";
        const isExpired =
          a?.dateExpiration && new Date(a.dateExpiration) < new Date();

        const totalAppels = a?.nbAppelCount ?? 0;
        const appelsQualifies = a?.nbAppelQualifieCount ?? 0;
        const appelsNonQualifies = Math.max(0, totalAppels - appelsQualifies);

        const totalWhatsapp = a?.nbWhatsappCount ?? 0;
        const whatsappQualifies = a?.nbWhatsappQualifieCount ?? 0;
        const whatsappNonQualifies = Math.max(
          0,
          totalWhatsapp - whatsappQualifies,
        );

        return {
          "Nom de l'article": a?.titre || "—",
          Catégorie: categorieName,
          "Nom du vendeur": getVendeurName(a),
          Numéro: a?.phone || "—",
          Ville: a?.ville || "—",
          Vues: a?.nbVueCount ?? 0,
          Favoris: a?.nbFavorisCount ?? 0,
          "Messages qualifiés": a?.nbMessageQualifieCount ?? 0,
          "Total appels": totalAppels,
          "Appels qualifiés": appelsQualifies,
          "Appels non qualifiés": appelsNonQualifies,
          "Total WhatsApp": totalWhatsapp,
          "WhatsApp qualifiés": whatsappQualifies,
          "WhatsApp non qualifiés": whatsappNonQualifies,
          Prix: a?.prix ?? "—",
          Abonnement: aboLabel(a?.typeAbonnement),
          Statut: computeArticleState(a).label,
          Expiré: isExpired ? "Oui" : "Non",
          "Créé le": formatDate(a?.createdAt),
        };
      });

      if (data.length === 0) {
        setAlert?.({
          type: "warning",
          message: "Aucune donnée à exporter.",
        });
        return;
      }

      const headers = Object.keys(data[0]);
      const csvRows = [
        headers.map(escapeCsvValue).join(";"),
        ...data.map((row) =>
          headers.map((h) => escapeCsvValue(row[h])).join(";"),
        ),
      ];

      // BOM pour un bon affichage des accents dans Excel
      const csvContent = "\uFEFF" + csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "articles_export.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setAlert?.({
        type: "success",
        message: "Export CSV terminé.",
      });
    } catch (error) {
      console.error(error);
      setAlert?.({
        type: "danger",
        message: "Erreur pendant l'export CSV.",
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleStatusFilter = (key) => {
    setStatusFilters((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const displayedArticles = articles;

  return (
    <div className="adminArticlesPage">
      <div className="container">
        <Breadcrumb items={breadcrumbItems} />

        <div className="adminArticlesGrid">
          <section className="adminArticlesCard adminArticlesCardCenter">
            <div className="adminArticlesHeader">
              <div className="adminArticlesHeaderLeft">
                <div className="adminArticlesTitle">Gestion des articles</div>
                <div className="adminArticlesSubtitle">
                  {loading ? "Chargement..." : `${total} article(s)`}
                </div>
              </div>

              <div className="adminArticlesHeaderRight">
                <button
                  type="button"
                  className="adminArticlesBtn adminArticlesBtnSoft"
                  onClick={handleExportCsv}
                  disabled={loading || articles.length === 0}
                  title="Exporter CSV"
                >
                  <i className="bi bi-file-earmark-spreadsheet" />
                  <span>Export CSV</span>
                </button>
                <button
                  type="button"
                  className="adminArticlesBtn adminArticlesBtnSoft"
                  onClick={() => fetchArticles(1)}
                  title="Rafraîchir"
                  disabled={loading}
                >
                  <i className="bi bi-arrow-clockwise" />
                  <span>Rafraîchir</span>
                </button>

                <Link
                  to="/article/creation"
                  className="adminArticlesBtn adminArticlesBtnPrimary"
                  title="Créer"
                >
                  <i className="bi bi-plus-lg" />
                  <span>Nouveau</span>
                </Link>
              </div>
            </div>

            {/* Légende des statuts */}
            {/* Légende des statuts — devenue filtre multi-sélection */}
            <div className="adminArticlesLegend">
              <span className="adminArticlesLegendTitle">Statuts</span>

              {Object.values(ARTICLE_STATES).map((s) => (
                <button
                  type="button"
                  key={s.key}
                  className={`adminArticlesLegendItem ${statusFilters.includes(s.key) ? "active" : ""}`}
                  onClick={() => toggleStatusFilter(s.key)}
                >
                  <i
                    className={`adminArticlesLegendDot dot${s.key.charAt(0).toUpperCase()}${s.key.slice(1)}`}
                  />
                  {s.label}
                </button>
              ))}

              {statusFilters.length > 0 && (
                <button
                  type="button"
                  className="adminArticlesLegendClear"
                  onClick={() => setStatusFilters([])}
                >
                  <i className="bi bi-x-lg" /> Réinitialiser
                </button>
              )}
            </div>

            {/* Filters */}
            <div className="adminArticlesFilters">
              <div className="adminArticlesFiltersRow">
                <div className="adminArticlesSearch">
                  <i className="bi bi-search" aria-hidden="true" />
                  <input
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Rechercher : titre, pseudo, description..."
                  />
                </div>

                <select
                  value={adminApprouved}
                  onChange={(e) => setAdminApprouved(e.target.value)}
                >
                  <option value="all">Tous les articles</option>
                  <option value="2">En attente de validation</option>
                  <option value="1">Approuvés</option>
                  <option value="0">Non approuvés</option>
                  <option value="3">Vendus</option>
                </select>

                <select
                  value={categories}
                  onChange={(e) => setCategories(e.target.value)}
                >
                  <option value="all">Catégorie</option>
                  {categoriesList.map((categorie) => (
                    <option key={categorie._id} value={categorie._id}>
                      {categorie.nom}
                    </option>
                  ))}
                </select>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="all">Statut</option>
                  <option value="1">Actifs</option>
                  <option value="2">Inactifs</option>
                </select>

                <select
                  value={archive}
                  onChange={(e) => setArchive(e.target.value)}
                >
                  <option value="all">Archive</option>
                  <option value="2">Non archivé</option>
                  <option value="1">Archivé</option>
                </select>

                {/* <select value={sort} onChange={(e) => setSort(e.target.value)}>
                  <option value="-createdAt">Plus récents</option>
                  <option value="createdAt">Plus anciens</option>
                  <option value="-prix">Prix ↓</option>
                  <option value="prix">Prix ↑</option>
                  <option value="titre">Titre A → Z</option>
                </select> */}

                {/* <select
                  value={expiration}
                  onChange={(e) => setExpiration(e.target.value)}
                >
                  <option value="all">Expiration</option>
                  <option value="active">Non expirés</option>
                  <option value="expired">Expirés</option>
                </select> */}

                <button
                  type="button"
                  className={`adminArticlesBtn adminArticlesBtnSoft ${showMoreFilters ? "isActive" : ""}`}
                  onClick={() => setShowMoreFilters((v) => !v)}
                >
                  <i className="bi bi-sliders" />
                  <span>Filtres avancés</span>
                  <i
                    className={`bi ${showMoreFilters ? "bi-chevron-up" : "bi-chevron-down"}`}
                  />
                </button>
              </div>

              {showMoreFilters && (
                <div className="adminArticlesFiltersRow adminArticlesFiltersAdvanced">
                  <div className="adminArticlesReactionMultiSelect">
                    <span className="adminArticlesReactionMultiSelectLabel">
                      Canal
                    </span>
                    {[
                      { value: "appels", label: "Appels" },
                      { value: "whatsapp", label: "WhatsApp" },
                      { value: "messages", label: "Messages" },
                    ].map((opt) => (
                      <label
                        key={opt.value}
                        className="adminArticlesReactionCheckbox"
                      >
                        <input
                          type="checkbox"
                          checked={reactionTypes.includes(opt.value)}
                          onChange={(e) => {
                            setReactionTypes((prev) =>
                              e.target.checked
                                ? [...prev, opt.value]
                                : prev.filter((v) => v !== opt.value),
                            );
                          }}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>

                  <div className="adminArticlesReactionMultiSelect">
                    <span className="adminArticlesReactionMultiSelectLabel">
                      Interaction
                    </span>
                    {[
                      { value: "vues", label: "Vues" },
                      { value: "favoris", label: "Favoris" },
                      { value: "facebook", label: "Partages Facebook" },
                    ].map((opt) => (
                      <label
                        key={opt.value}
                        className="adminArticlesReactionCheckbox"
                      >
                        <input
                          type="checkbox"
                          checked={reactionTypes.includes(opt.value)}
                          onChange={(e) => {
                            setReactionTypes((prev) =>
                              e.target.checked
                                ? [...prev, opt.value]
                                : prev.filter((v) => v !== opt.value),
                            );
                          }}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>

                  <select
                    value={reactionLevel}
                    onChange={(e) => setReactionLevel(e.target.value)}
                    disabled={reactionTypes.length === 0}
                  >
                    <option value="all">Tous niveaux</option>
                    <option value="low">Peu populaire (5+)</option>
                    <option value="medium">Populaire (20+)</option>
                    <option value="high">Très populaire (50+)</option>
                  </select>
                  {/* <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                  >
                    <option value="-createdAt">Plus récents</option>
                    <option value="createdAt">Plus anciens</option>
                    <option value="-prix">Prix ↓</option>
                    <option value="prix">Prix ↑</option>
                    <option value="titre">Titre A → Z</option>
                    {reactionTypes.length > 0 && (
                      <>
                        <option value="-reactionScore">
                          Réactions ↓ (plus → moins)
                        </option>
                        <option value="reactionScore">
                          Réactions ↑ (moins → plus)
                        </option>
                      </>
                    )}
                  </select> */}

                  {/* <select
                    value={etat}
                    onChange={(e) => setEtat(e.target.value)}
                  >
                    <option value="all">État</option>
                    <option value="1">Neuf</option>
                    <option value="2">Occasion</option>
                  </select> */}

                  {/* <select
                    value={typeOffre}
                    onChange={(e) => setTypeOffre(e.target.value)}
                  >
                    <option value="all">Type</option>
                    <option value="1">Offre</option>
                    <option value="2">Demande</option>
                  </select> */}

                  {/* <select
                    value={typeAbonnement}
                    onChange={(e) => setTypeAbonnement(e.target.value)}
                  >
                    <option value="all">Abonnement</option>
                    <option value="1">Premium</option>
                    <option value="2">Standard</option>
                  </select> */}

                  <select
                    value={periode}
                    onChange={(e) => {
                      const val = e.target.value;
                      setPeriode(val);
                      if (val !== "custom") {
                        setDateDebut("");
                        setDateFin("");
                      }
                    }}
                  >
                    <option value="all">Période</option>
                    <option value="today">Aujourd'hui</option>
                    <option value="7j">7 derniers jours</option>
                    <option value="30j">30 derniers jours</option>
                    <option value="month">Ce mois-ci</option>
                    <option value="custom">Personnalisée</option>
                  </select>

                  {/* <div
                    className="adminArticlesAutocomplete"
                    onBlur={() => {
                      setTimeout(() => setShowUserDropdown(false), 150);
                    }}
                  >
                    <input
                      type="text"
                      placeholder="Vendeur"
                      value={userSearch}
                      onFocus={() => setShowUserDropdown(true)}
                      onChange={(e) => {
                        setUserSearch(e.target.value);
                        setShowUserDropdown(true);
                        if (e.target.value.trim() === "") {
                          setUserSelected("all");
                        }
                      }}
                    />

                    {showUserDropdown && (
                      <div className="adminArticlesAutocompleteDropdown">
                        <div
                          className="adminArticlesAutocompleteItem"
                          onClick={() => {
                            setUserSelected("all");
                            setUserSearch("");
                            setShowUserDropdown(false);
                          }}
                        >
                          Tous les vendeurs
                        </div>

                        {usersList
                          .filter((user) =>
                            `${user.nom} ${user.prenom}`
                              .toLowerCase()
                              .includes(userSearch.toLowerCase()),
                          )
                          .map((user) => (
                            <div
                              key={user._id}
                              className="adminArticlesAutocompleteItem"
                              onClick={() => {
                                setUserSelected(user._id);
                                setUserSearch(`${user.nom} ${user.prenom}`);
                                setShowUserDropdown(false);
                              }}
                            >
                              {user.nom} {user.prenom}
                            </div>
                          ))}
                      </div>
                    )}
                  </div> */}

                  {/* <select
                    value={typeCompteFilter}
                    onChange={(e) => setTypeCompteFilter(e.target.value)}
                  >
                    <option value="all">Tous types</option>
                    <option value="particulier">Particulier</option>
                    <option value="professionnel">Professionnel</option>
                    <option value="test">Test</option>
                  </select> */}

                  {periode === "custom" && (
                    <div className="adminArticlesFiltersRow adminArticlesDateRange">
                      <div className="adminArticlesDateField">
                        <label>Du</label>
                        <input
                          type="date"
                          value={dateDebut}
                          onChange={(e) => setDateDebut(e.target.value)}
                        />
                      </div>
                      <div className="adminArticlesDateField">
                        <label>Au</label>
                        <input
                          type="date"
                          value={dateFin}
                          onChange={(e) => setDateFin(e.target.value)}
                        />
                      </div>

                      {(dateDebut || dateFin) && (
                        <button
                          type="button"
                          className="adminArticlesDateResetBtn"
                          onClick={() => {
                            setDateDebut("");
                            setDateFin("");
                            setPeriode("all");
                          }}
                          title="Réinitialiser la période personnalisée"
                        >
                          <i className="bi bi-x-lg" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Body */}
            {loading ? (
              <div className="adminArticlesEmpty">
                <div className="adminArticlesSpinner" />
                <div>Chargement des articles...</div>
              </div>
            ) : displayedArticles.length === 0 ? (
              <div className="adminArticlesEmpty">
                <i className="bi bi-funnel" />
                <div>Aucun article ne correspond aux statuts sélectionnés.</div>
              </div>
            ) : (
              <div className="adminArticlesTableWrap">
                <table className="adminArticlesTable">
                  <thead>
                    <tr>
                      <th>Article</th>
                      <th>Statut</th>
                      <th>Paiement</th>
                      <th className="hideOnMobile">Catégorie</th>
                      <th className="hideOnMobile">Vendeur</th>
                      <th>Réactions</th>
                      <th className="hideOnMobile">Numéro</th>
                      {/* <th className="hideOnMobile">Ville</th> */}
                      {/* <th>Abonnement</th> */}
                      <th className="hideOnMobile">Créé</th>
                      {user.role === 1 && (
                        <th className="colActions">Actions</th>
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {displayedArticles.map((a) => {
                      const title = a?.titre || "—";
                      const idArticle = a?._id || "—";
                      const photo =
                        resolveArticlePhoto(a?.photos) + `?v=${Date.now()}`;
                      const isActive = Number(a?.status) === 1;
                      const isArchived = Number(a?.archive) === 1;
                      const categorieName =
                        a?.idCategorie?.nom || a?.categorie?.nom || "—";

                      const vendeurName =
                        a?.idUtilisateur?.nom || a?.utilisateur?.nom
                          ? `${a?.idUtilisateur?.nom || a?.utilisateur?.nom || ""} ${a?.idUtilisateur?.prenom ||
                            a?.utilisateur?.prenom ||
                            ""
                            }`.trim()
                          : a?.pseudo || "—";

                      const state = computeArticleState(a);
                      const rowStateClass =
                        state.key === "test"
                          ? "rowTest"
                          : state.key === "sold"
                            ? "rowSold"
                            : state.key === "expired"
                              ? "rowExpired"
                              : "";

                      return (
                        <tr
                          key={a?._id}
                          className={rowStateClass}
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();

                            navigate(`/article/modification?id=${a?._id}`, {
                              state: {
                                from: location.pathname + location.search,
                              },
                            });
                          }}
                        >
                          <td>
                            <div className="adminArticlesCellItem">
                              <div className="adminArticlesThumb">
                                <img
                                  src={photo}
                                  alt={title}
                                  loading="lazy"
                                  onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = defaultArticleImage;
                                  }}
                                />
                              </div>

                              <div className="adminArticlesCellItemText">
                                <div className="adminArticlesName">
                                  {truncateText(title)}
                                  {/* {idArticle} */}
                                </div>

                                <div className="adminArticlesMobileMeta">
                                  <div>
                                    <i className="bi bi-tag" /> {categorieName}
                                  </div>
                                  <div>
                                    <i className="bi bi-person" /> {vendeurName}
                                  </div>
                                  <div>
                                    <i className="bi bi-geo-alt" />{" "}
                                    {a?.ville || "—"}
                                  </div>
                                  <div>
                                    <i className="bi bi-cash-coin" />{" "}
                                    {a?.prix ?? "—"} Ar
                                  </div>
                                  <div>
                                    <i className="bi bi-calendar3" />{" "}
                                    {formatDate(a?.createdAt)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>

                          <td>
                            <span
                              className={`adminArticlesBadge ${state.cls}`}
                              title={state.label}
                            >
                              <i className={`bi ${state.icon}`} /> {state.label}
                            </span>
                          </td>

                          <td>
                            {a?.paiements?.length > 0 ? (
                              <button
                                type="button"
                                className={`adminArticlesBadge adminArticlesBadgeClickable ${paiementStatusBadgeClass(a.paiements[0].status)}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  openPaiementModal(a);
                                }}
                                title="Voir l'historique des paiements"
                              >
                                {/* <i className="bi bi-credit-card" /> */}
                                {paiementStatusLabel(a.paiements[0].status)}
                                {a.paiements.length > 1 && (
                                  <span className="adminArticlesPaiementCount">
                                    {a.paiements.length}
                                  </span>
                                )}
                              </button>
                            ) : (
                              <span className="adminArticlesBadge badgeNoPaiement">
                                <i className="bi bi-dash-circle" /> Aucun paiement
                              </span>
                            )}
                          </td>

                          <td className="muted hideOnMobile">
                            {categorieName}
                          </td>
                          <td className="muted hideOnMobile">{vendeurName}</td>
                          <td className="adminArticlesReactions">
                            <div className="adminArticlesReactionCol">
                              <span className="adminArticlesReactionColTitle">
                                Interaction
                              </span>
                              <div className="adminArticlesReactionRow">
                                <span
                                  className="adminArticlesReactionPill"
                                  title="Vues"
                                >
                                  <i className="bi bi-eye icon-vue" />
                                  {a?.nbVueCount ?? 0}
                                </span>
                                <span
                                  className="adminArticlesReactionPill"
                                  title="Favoris"
                                >
                                  <i className="bi bi-heart icon-favori" />
                                  {a?.nbFavorisCount ?? 0}
                                </span>
                                {/* <span
                                  className="adminArticlesReactionPill"
                                  title="Partages Facebook"
                                >
                                  <i className="bi bi-facebook icon-facebook" />
                                  {a?.nbPartageFacebookCount ?? 0}
                                </span> */}
                              </div>
                            </div>
                            <div className="adminArticlesReactionCol">
                              <span className="adminArticlesReactionColTitle">
                                Canal
                              </span>
                              <div className="adminArticlesReactionRow">
                                <span
                                  className="adminArticlesReactionPill"
                                  title="Appels qualifiés / reçus"
                                >
                                  <i className="bi bi-telephone icon-appel" />
                                  {a?.nbAppelQualifieCount ?? 0}/
                                  {a?.nbAppelCount ?? 0}
                                </span>
                                <span
                                  className="adminArticlesReactionPill"
                                  title="Messages qualifiés / reçus"
                                >
                                  <i className="bi bi-chat-dots icon-message" />
                                  {a?.nbMessageQualifieCount ?? 0}/
                                  {a?.nbMessageCount ?? 0}
                                </span>
                                <span
                                  className="adminArticlesReactionPill"
                                  title="WhatsApp qualifiés / reçus"
                                >
                                  <i className="bi bi-whatsapp icon-whatsapp" />
                                  {a?.nbWhatsappQualifieCount ?? 0}/
                                  {a?.nbWhatsappCount ?? 0}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="muted hideOnMobile">
                            {a?.phone || "—"}
                          </td>
                          {/* <td className="muted hideOnMobile">
                            {a?.ville || "—"}
                          </td> */}

                          {/* <td>
                            <span
                              className={`adminArticlesBadge ${aboBadgeClass(a?.typeAbonnement)}`}
                            >
                              {aboLabel(a?.typeAbonnement)}
                            </span>
                          </td> */}

                          <td className="muted hideOnMobile">
                            {formatDate(a?.createdAt)}
                          </td>

                          {user.role === 1 && (
                            <td className="colActions">
                              <div className="adminArticlesActions">
                                <button
                                  type="button"
                                  className={`adminArticlesIconBtn ${isActive ? "success" : "danger"}`}
                                  title={isActive ? "Désactiver" : "Activer"}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    openConfirmModal("status", a);
                                  }}
                                >
                                  <i
                                    className={`bi ${isActive ? "bi-lock" : "bi-unlock"}`}
                                  />
                                </button>

                                <button
                                  type="button"
                                  className={`adminArticlesIconBtn ${isArchived ? "warning" : ""}`}
                                  title={
                                    isArchived ? "Désarchiver" : "Archiver"
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    openConfirmModal("archive", a);
                                  }}
                                >
                                  <i
                                    className={`bi ${isArchived ? "bi-arrow-counterclockwise" : "bi-archive"}`}
                                  />
                                </button>

                                <button
                                  type="button"
                                  className={`adminArticlesIconBtn ${stateIconBtnClass(state.key)}`}
                                  title={`Statut : ${state.label}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();

                                    if (approvalMenuId === a._id) {
                                      setApprovalMenuId(null);
                                      setApprovalMenuPos(null);
                                      return;
                                    }

                                    const rect =
                                      e.currentTarget.getBoundingClientRect();
                                    const menuHeight = 230; // hauteur approx. du menu (5 items)
                                    const openUp =
                                      window.innerHeight - rect.bottom <
                                      menuHeight;

                                    setApprovalMenuPos({
                                      top: openUp
                                        ? rect.top - 8
                                        : rect.bottom + 8,
                                      left: rect.right,
                                      openUp,
                                    });
                                    setApprovalMenuId(a._id);
                                  }}
                                >
                                  <i className={`bi ${state.icon}`} />
                                </button>

                                {approvalMenuId &&
                                  approvalMenuPos &&
                                  approvalMenuArticle &&
                                  createPortal(
                                    <div
                                      className="approval-dropdown"
                                      style={{
                                        top: approvalMenuPos.top,
                                        left: approvalMenuPos.left,
                                        transform: `translate(-100%, ${approvalMenuPos.openUp ? "-100%" : "0"})`,
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {loadginApproval ? (
                                        <div className="adminArticlesSpinner" />
                                      ) : (
                                        <>
                                          <div
                                            className="approval-item success"
                                            onClick={() =>
                                              handleSetApproval(
                                                approvalMenuArticle,
                                                1,
                                              )
                                            }
                                          >
                                            ✅ Approuver
                                          </div>
                                          <div
                                            className="approval-item danger"
                                            onClick={() =>
                                              handleSetApproval(
                                                approvalMenuArticle,
                                                0,
                                              )
                                            }
                                          >
                                            ❌ Non approuver
                                          </div>
                                          <div
                                            className="approval-item warning"
                                            onClick={() =>
                                              handleSetApproval(
                                                approvalMenuArticle,
                                                2,
                                              )
                                            }
                                          >
                                            ⏳ En attente
                                          </div>
                                          <div
                                            className="approval-item info"
                                            onClick={() =>
                                              handleNotInterested(
                                                approvalMenuArticle,
                                                4,
                                              )
                                            }
                                          >
                                            ℹ️ Plus intéressé
                                          </div>
                                          <div
                                            className="approval-item sold"
                                            onClick={() =>
                                              handleSetApproval(
                                                approvalMenuArticle,
                                                3,
                                              )
                                            }
                                          >
                                            🏷️ Vendu
                                          </div>
                                        </>
                                      )}
                                    </div>,
                                    document.body,
                                  )}
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination serveur */}
            {!loading && total > 0 && (
              <div className="adminArticlesPagination">
                <button
                  type="button"
                  className="adminArticlesPageBtn"
                  onClick={() => fetchArticles(Math.max(1, safePage - 1))}
                  disabled={safePage <= 1}
                >
                  <i className="bi bi-chevron-left" />
                </button>

                <div className="adminArticlesPageInfo">
                  Page <b>{safePage}</b> / {totalPages}
                </div>

                <button
                  type="button"
                  className="adminArticlesPageBtn"
                  onClick={() =>
                    fetchArticles(Math.min(totalPages, safePage + 1))
                  }
                  disabled={safePage >= totalPages}
                >
                  <i className="bi bi-chevron-right" />
                </button>
              </div>
            )}
          </section>
        </div>
        {confirmModal.open && (
          <div className="adminArticlesModalOverlay">
            <div className="adminArticlesModal">
              <div className="adminArticlesModalIcon">
                <i
                  className={`bi ${confirmModal.type === "archive"
                    ? "bi-archive"
                    : Number(confirmModal.article?.status) === 1
                      ? "bi-exclamation-triangle"
                      : "bi-check-circle"
                    }`}
                />
              </div>

              <h3>
                {confirmModal.type === "status"
                  ? Number(confirmModal.article?.status) === 1
                    ? "Désactiver cet article ?"
                    : "Activer cet article ?"
                  : Number(confirmModal.article?.archive) === 1
                    ? "Désarchiver cet article ?"
                    : "Archiver cet article ?"}
              </h3>

              <p>
                {confirmModal.type === "status"
                  ? Number(confirmModal.article?.status) === 1
                    ? "Cet article ne sera plus visible comme article actif."
                    : "Cet article sera de nouveau visible comme article actif."
                  : Number(confirmModal.article?.archive) === 1
                    ? "Cet article sera retiré des archives."
                    : "Cet article sera placé dans les archives."}
              </p>

              <div className="adminArticlesModalArticle">
                {confirmModal.article?.titre || "Article"}
              </div>

              <div className="adminArticlesModalActions">
                <button
                  type="button"
                  className="adminArticlesModalCancel"
                  onClick={closeConfirmModal}
                >
                  Annuler
                </button>

                <button
                  type="button"
                  className={
                    confirmModal.type === "archive"
                      ? "adminArticlesModalConfirm warning"
                      : Number(confirmModal.article?.status) === 1
                        ? "adminArticlesModalConfirm danger"
                        : "adminArticlesModalConfirm success"
                  }
                  onClick={confirmAction}
                >
                  {confirmModal.type === "status"
                    ? Number(confirmModal.article?.status) === 1
                      ? "Désactiver"
                      : "Activer"
                    : Number(confirmModal.article?.archive) === 1
                      ? "Désarchiver"
                      : "Archiver"}
                </button>
              </div>
            </div>
          </div>
        )}

        {paiementModal.open && (
          <div className="adminArticlesModalOverlay" onClick={closePaiementModal} >
            <div
              className="adminArticlesModal adminArticlesPaiementModal"
              onClick={(e) => e.stopPropagation()}
              style={{
            "height": "90vh",
            "overflow-Y": "auto"
          }}
            >
              <div className="adminArticlesModalIcon">
                <i className="bi bi-credit-card" />
              </div>

              <h3>Historique des paiements</h3>

              <div className="adminArticlesModalArticle">
                {paiementModal.article?.titre || "Article"}
              </div>

              {(() => {
                const list = paiementModal.article?.paiements || [];
                console.log(list);
                if (list.length === 0) return <p>Aucun paiement enregistré.</p>;

                return (
                  <div className="adminArticlesPaiementList">
                    {list.map((p, idx) => {
                      const remise = toNumber(p.amountRemise);
                      const initialRaw = toNumber(p.amountInitial);
                      const initial =
                        initialRaw > 0 ? initialRaw : toNumber(p.amount) + remise;
                      const showDiscount = hasDiscountInfo(p);
                      const paiementDate = p.date || p.createdAt;
                      const sourcePhoneNumber = p.sourcePhoneNumber || "—";

                      return (
                        <div
                          className="adminArticlesPaiementCard"
                          key={p._id || idx}
                        >
                          <div className="adminArticlesPaiementCardHeader">
                            <span
                              className={`adminArticlesBadge ${paiementStatusBadgeClass(p.status)}`}
                            >
                              {paiementStatusLabel(p.status)}
                            </span>
                            <span className="adminArticlesPaiementCardDate">
                              {formatDate(paiementDate)}
                            </span>
                          </div>

                          <div className="adminArticlesPaiementDetails">
                            <div className="adminArticlesPaiementRow">
                              <span>Type</span>
                              <b>{paiementTypeLabel(p.type)}</b>
                            </div>

                            <div className="adminArticlesPaiementRow">
                              <span>Méthode</span>
                              <b>{paiementMethodLabel(p.methodPayment)}</b>
                            </div>

                            {sourcePhoneNumber && (
                              <div className="adminArticlesPaiementRow">
                                <span>Numéro source</span>
                                <b>{sourcePhoneNumber}</b>
                              </div>
                            )}

                            {showDiscount && (
                              <div className="adminArticlesPaiementRow">
                                <span>Prix initial</span>
                                <b>
                                  {initial} {p.devise}
                                </b>
                              </div>
                            )}

                            {remise > 0 && (
                              <div className="adminArticlesPaiementRow">
                                <span>Remise{p.coupon ? ` (${p.coupon})` : ""}</span>
                                <b>
                                  - {remise} {p.devise}
                                </b>
                              </div>
                            )}

                            {!remise && p.coupon && (
                              <div className="adminArticlesPaiementRow">
                                <span>Coupon</span>
                                <b>{p.coupon}</b>
                              </div>
                            )}

                            <div className="adminArticlesPaiementRow adminArticlesPaiementTotal">
                              <span>Montant payé</span>
                              <b>
                                {p.amount} {p.devise}
                              </b>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              <div className="adminArticlesModalActions">
                <button
                  type="button"
                  className="adminArticlesModalCancel"
                  onClick={closePaiementModal}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
